import type { CSSProperties, FC, PropsWithChildren } from 'react';
import { useMemo, useDeferredValue, useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

import styles from './popup.module.less';

const canIUseDocBody = typeof document !== 'undefined' && Boolean(document?.body);

export type SinglePopupProps = {
  className?: string;
  style?: CSSProperties;
  bodyClassName?: string;
  bodyStyle?: CSSProperties;
  visible?: boolean;
  destroyOnClose?: boolean;
  onMaskClick?: () => void;
};

const SinglePopup: FC<PropsWithChildren<SinglePopupProps>> = (props) => {
  const { className, style, bodyClassName, bodyStyle, visible, destroyOnClose, onMaskClick, children } = props;

  const deferredVisible = useDeferredValue(visible);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [nativeVisible, setNativeVisible] = useState(visible);

  const childrenNode = useMemo(() => {
    if (destroyOnClose) {
      if (nativeVisible) {
        return children;
      } else {
        return null;
      }
    }
    return children;
  }, [children, destroyOnClose, nativeVisible]);

  useEffect(() => {
    if (canIUseDocBody) {
      if (deferredVisible) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
    return () => {
      if (canIUseDocBody) {
        document.body.style.overflow = '';
      }
    };
  }, [deferredVisible]);

  useEffect(() => {
    if (visible) {
      setNativeVisible(true);
    } else {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setNativeVisible(false);
      }, 200);
    }
  }, [visible]);
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div data-visible={visible || nativeVisible} className={classNames(styles.wrapper, className)} style={style}>
      <div data-visible={visible} className={styles.popupMask} onClick={onMaskClick} />
      <div data-visible={deferredVisible} className={classNames(styles.popupBody, bodyClassName)} style={bodyStyle}>
        {childrenNode}
      </div>
    </div>
  );
};

export default SinglePopup;
